import { useEffect, useState } from "react";
import ModernModal from "./EditModal";
import PhotosUploader from "./PhotosUploader";
import toast from "react-hot-toast";

import { motion } from "framer-motion";

import { DndContext, closestCenter } from "@dnd-kit/core";

import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy
} from "@dnd-kit/sortable";

import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

function SortableItem({ id, photo, onDelete }) {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <motion.div ref={setNodeRef} style={style} layout className="photo-card">
      <div className="drag-handle" {...attributes} {...listeners}>
        <img src={photo} alt="" />
      </div>

      <button
        className="delete-btn"
        onClick={(e) => {
          e.stopPropagation();
          onDelete(id);
        }}
      >
        ✕
      </button>
    </motion.div>
  );
}

export default function PhotosManager({ data, hotelId, refreshData }) {
  const [isOpen, setIsOpen] = useState(false);
  const [listPhotos, setListPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (Array.isArray(data?.photos)) {
      setListPhotos(data.photos.filter((p) => typeof p === "string" && p.trim() !== ""));
    } else {
      setListPhotos([]);
    }
  }, [data]);

  const handlePhotos = async (photosArray) => {
    const clean = photosArray.filter((p) => typeof p === "string" && p.trim() !== "");

    try {
      const res = await fetch(`https://api.komorotel.com/api/hotels/${hotelId}`, {
        method: "PUT",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ photos: clean }),
      });

      if (!res.ok) throw new Error("Enregistrement impossible");

      if (refreshData) refreshData();
    } catch (err) {
      console.error(err);
      toast.error("Les photos n'ont pas pu être enregistrées");
    }
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = listPhotos.indexOf(active.id);
    const newIndex = listPhotos.indexOf(over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const newOrder = arrayMove(listPhotos, oldIndex, newIndex);
    setListPhotos(newOrder);
    handlePhotos(newOrder);
  };

  const uploadToCloudinary = async (file) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("upload_preset", "komorotel_unsigned");

    const res = await fetch(
      "https://api.cloudinary.com/v1_1/dhwqnwnvh/image/upload",
      {
        method: "POST",
        body: formData,
      }
    );

    const result = await res.json();

    if (!res.ok || !result.secure_url) {
      throw new Error(result?.error?.message || "Upload échoué");
    }

    return result.secure_url;
  };

  const handleUpload = async (files) => {
    if (!files || files.length === 0) return;

    setUploading(true);

    const results = await Promise.allSettled(
      files.map((file) => uploadToCloudinary(file))
    );

    const uploadedUrls = results
      .filter((r) => r.status === "fulfilled" && typeof r.value === "string")
      .map((r) => r.value);

    const failed = results.length - uploadedUrls.length;
    if (failed > 0) {
      toast.error(`${failed} photo(s) n'ont pas pu être envoyées`);
    }

    setUploading(false);

    if (uploadedUrls.length === 0) return;

    const newPhotos = [...listPhotos, ...uploadedUrls];
    setListPhotos(newPhotos);
    await handlePhotos(newPhotos);

    setIsOpen(false);
  };

  const handleDelete = (photoUrl) => {
    const newPhotos = listPhotos.filter((p) => p !== photoUrl);
    setListPhotos(newPhotos);
    handlePhotos(newPhotos);
  };

  return (
    <div className="modern-card">
      <div className="section-title d-flex justify-content-between">
        Photos
        <button
          className="modern-button modern-button--sm"
          onClick={() => setIsOpen(true)}
        >
          + Ajouter
        </button>
      </div>

      <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={listPhotos} strategy={verticalListSortingStrategy}>
          <div className="photo-grid">
            {listPhotos.map((photo) => (
              <SortableItem
                key={photo}
                id={photo}
                photo={photo}
                onDelete={handleDelete}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>

      {listPhotos.length === 0 && (
        <p className="eq-empty">Aucune photo pour l'instant.</p>
      )}

      <ModernModal isOpen={isOpen} onClose={() => setIsOpen(false)}>
        <h4>Ajouter des photos</h4>
        {uploading ? (
          <div className="modern-loader" />
        ) : (
          <PhotosUploader onUpload={handleUpload} />
        )}
      </ModernModal>
    </div>
  );
}